import { WeatherSources } from '../models/WeatherSources.model'

// sources whose weatherSourceId is a "lat,lon" coordinate pair rather than a station id
export const COORDINATE_WEATHER_SOURCES: WeatherSources[] = [
  WeatherSources.Mrms,
  WeatherSources.OpenWeather,
  WeatherSources.OpenMeteo,
]

export const isCoordinateWeatherSource = (weatherSource?: WeatherSources): boolean =>
  !!weatherSource && COORDINATE_WEATHER_SOURCES.includes(weatherSource)

/**
 * Validates a weatherSourceId for the given source. Returns an error message
 * to show the admin, or null when the value is valid. Shared by the weather
 * group form (create and edit) and the CSV import so no write path can save
 * an id the hourly gather job would choke on.
 */
export const validateWeatherSourceId = (weatherSource?: WeatherSources, weatherSourceId?: string): string | null => {
  if (!weatherSourceId?.trim()) {
    return 'Weather Source Id is required'
  }
  if (!isCoordinateWeatherSource(weatherSource)) {
    return null
  }

  const parts = weatherSourceId.split(',')
  if (parts.length !== 2 || parts.some((part) => !part.trim())) {
    return 'Weather Source Id must be GPS coordinates as "lat,lon" eg. 36.3729,-94.2088'
  }
  const latitude = Number(parts[0])
  const longitude = Number(parts[1])
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return 'Weather Source Id must be GPS coordinates as "lat,lon" eg. 36.3729,-94.2088'
  }
  if (latitude < -90 || latitude > 90) {
    return 'Latitude must be between -90 and 90'
  }
  if (longitude < -180 || longitude > 180) {
    return 'Longitude must be between -180 and 180'
  }
  return null
}

/**
 * Import-specific wrapper around validateWeatherSourceId. CSV imports support
 * partial updates (e.g. { id, name }), so a row that touches neither
 * weatherSource nor weatherSourceId is left alone. A row that provides either
 * field gets the full validation — including source-without-id, which must
 * fail so a source change can never ride along with a stale id.
 */
export const validateWeatherSourceIdForImport = (
  weatherSource?: WeatherSources,
  weatherSourceId?: string,
): string | null => {
  const providesSource = !!`${weatherSource ?? ''}`.trim()
  const providesId = !!weatherSourceId?.trim()
  if (!providesSource && !providesId) {
    return null
  }
  return validateWeatherSourceId(weatherSource, weatherSourceId)
}

/**
 * The weatherSourceId to keep when the source selection changes: a station id
 * is meaningless to a coordinate source (and vice versa), so any change of
 * source clears the id and forces the admin to enter a compatible one.
 */
export const weatherSourceIdOnSourceChange = (
  previousSource: WeatherSources | undefined,
  newSource: WeatherSources,
  currentId: string | undefined,
): string => {
  return newSource === previousSource ? currentId || '' : ''
}
